import React, { FC, useEffect } from 'react';

import {
  adminActions,
  getPendingCancellationDetails,
  pendingCancellationDetails,
  PendingCancellationTabTypes,
} from '@pharmaplan/common';
import { Box } from '@mui/material';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import strings from '../../../../localization';
import CustomGridLayout from '../../../common/CustomGridLayout';
import ServiceCard from '../../../SelfService/ServiceCard';
import {
  setFooter,
  setFooterShow,
  setTitle,
} from '../../../../actions/drawerActions';
import usePendingCancellationActions from '../../../../hooks/Admin/PendingCancellations/usePendingCancellationActions';
import usePendingCancellationDetails from '../../../../hooks/Admin/PendingCancellations/usePendingCancellationDetails';
import { nameLabels } from '../../../../helpers/Constants';
import DynamicTable from '../../../DynamicTable';

import styles from './styles';

interface IPendingCancellationDetails {
  cancellationRequestId: string;
  type: PendingCancellationTabTypes;
}

const { rejectedCancellations, cancelledBookings } = PendingCancellationTabTypes;

const successAction = adminActions.getPendingCancellationDetails;

const PendingCancellationDetails: FC<IPendingCancellationDetails> = ({
  cancellationRequestId,
  type,
}) => {
  const dispatch = useAppDispatch();

  const cancellationDetails = useAppSelector(pendingCancellationDetails);
  const { approveAction, rejectAction } = usePendingCancellationActions();
  const {
    table,
    success,
    requestCancellationConfig,
    cancellationInfoConfig,
    rejectionInfoConfig,
    bookingInfo,
  } = usePendingCancellationDetails();

  const { requestCode, pharmacistName, bookings, pharmacyName } = cancellationDetails ?? {};

  useEffect(() => {
    dispatch(
      getPendingCancellationDetails({ id: cancellationRequestId, type }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const longTermBookingInfo = [
    { key: strings.pharmacyName, value: pharmacyName },
    { key: nameLabels().nameLabel, value: pharmacistName },
  ];

  const topGrid = {
    [cancelledBookings]: {
      config: cancellationInfoConfig,
      title: strings.cancellationInfo,
    },
    [rejectedCancellations]: {
      config: rejectionInfoConfig,
      title: strings.rejectedCancellationInfo,
    },
  };

  const handleFooter = () => {
    dispatch(
      setFooter({
        show: true,
        pagination: {
          show: false,
        },
        buttons: {
          show: true,
          primaryButton: {
            label: strings.approve,
            onClick: () =>
              approveAction(cancellationRequestId, requestCode),
          },
          secondaryButton: {
            label: strings.reject,
            onClick: () =>
              rejectAction(cancellationRequestId, requestCode),
          },
        },
      }),
    );
  };

  useEffect(() => {
    const title = `${strings.code}: ${requestCode}`;
    dispatch(setTitle({ title }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCode]);

  useEffect(() => {
    if (type === PendingCancellationTabTypes.pendingCancellations) {
      handleFooter();
    } else {
      dispatch(setFooterShow(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancellationDetails]);

  return (
    <>
      {topGrid[type as keyof typeof topGrid] && (
        <ServiceCard
          customContainerStyle={styles.serviceContainer}
          content={(
            <CustomGridLayout
              successAction={successAction}
              title={topGrid[type as keyof typeof topGrid].title}
              config={topGrid[type as keyof typeof topGrid].config}
            />
          )}
        />
      )}
      <ServiceCard
        customContainerStyle={styles.serviceContainer}
        content={(
          <CustomGridLayout
            successAction={successAction}
            title={strings.requestCancellationInfo}
            config={requestCancellationConfig}
          />
        )}
      />
      <ServiceCard
        customContainerStyle={styles.serviceContainer}
        content={
          bookings?.length ? (
            <>
              <CustomGridLayout
                successAction={successAction}
                title={strings.bookingInfo}
                config={longTermBookingInfo}
              />
              <Box sx={styles.tableContainer}>
                <DynamicTable
                  hidePagination
                  showHeader={false}
                  noContainerStyling
                  stickyHeader
                  table={table}
                  loadSuccess={success}
                />
              </Box>
            </>
          ) : (
            <CustomGridLayout
              successAction={successAction}
              title={strings.bookingInfo}
              config={bookingInfo}
            />
          )
        }
      />
    </>
  );
};

export default PendingCancellationDetails;
